import React from "react";
import PropTypes from "prop-types";
import { withStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import utils from "../../universal/utils";

const styles = (theme) => ({
  root: {
    margin: theme.spacing(2),
    marginTop: 35,
  },
  paper: {
    padding: theme.spacing(3),
    textAlign: "center",
  },
  successBox: {
    background: "#e8f5e9",
    border: "2px solid #4caf50",
    borderRadius: 8,
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  errorBox: {
    background: "#ffebee",
    border: "2px solid #f44336",
    borderRadius: 8,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  cancelledBox: {
    background: "#fff3e0",
    border: "2px solid #ff9800",
    borderRadius: 8,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  actions: {
    display: "flex",
    justifyContent: "center",
    gap: theme.spacing(2),
    flexWrap: "wrap",
  },
  "@media (min-width: 1024px)": {
    root: {
      width: 700,
      marginLeft: "auto",
      marginRight: "auto",
      marginTop: 90,
    },
  },
});

const basePath = utils.getEdgeBasePath();

function formatAmount(amount) {
  return `₪${Number(amount || 0).toFixed(2)}`;
}

function paramsToObject(params) {
  const data = {};
  params.forEach((value, key) => {
    data[key] = value;
  });
  return data;
}

class PaymentLinkResultPage extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      mounted: false,
      loading: false,
      result: null,
      error: "",
      retryPath: "/",
    };
  }

  componentDidMount() {
    const retryPath =
      typeof window !== "undefined" && window.sessionStorage
        ? window.sessionStorage.getItem("paymentLinkLastPath") || "/"
        : "/";

    this.setState({ mounted: true, retryPath }, () => {
      this.handleReturn();
    });
  }

  handleReturn = async () => {
    const path = window.location.pathname;
    const params = new URLSearchParams(window.location.search);
    const payload = {
      uniqueID: params.get("uniqueID"),
      personalId: params.get("personalId"),
      cardMask: params.get("cardMask"),
      txId: params.get("txId"),
      authNumber: params.get("authNumber"),
      responseMAC: params.get("responseMAC"),
      traceId:
        window.crypto && window.crypto.randomUUID
          ? window.crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(16).slice(2)}`,
    };

    if (path === "/payment-link/success") {
      this.setState({ loading: true, error: "" });

      try {
        const response = await fetch(`${basePath}/api/v1/payment-links/finalize`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        const data = await response.json();

        if (!response.ok || !data.ok) {
          throw new Error("אירעה שגיאה בעת אישור התשלום.");
        }

        this.setState({ result: data });
      } catch (error) {
        console.error("Error finalizing payment link payment:", error);
        this.setState({
          error: error.message || "אירעה שגיאה בעת אישור התשלום.",
        });
      } finally {
        this.setState({ loading: false });
      }

      return;
    }

    const status =
      path === "/payment-link/failed" ? "failed" : "cancelled";

    try {
      await fetch(`${basePath}/api/v1/payment-links/return-status`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          uniqueID: payload.uniqueID,
          txId: payload.txId,
          status,
          data: paramsToObject(params),
        }),
      });
    } catch (error) {
      console.error("Error recording payment return status:", error);
    }
  };

  goToRetry = () => {
    window.location.href = this.state.retryPath || "/";
  };

  render() {
    const { classes } = this.props;
    const { mounted, loading, result, error } = this.state;

    if (!mounted || loading) {
      return (
        <div className={classes.root}>
          <Paper className={classes.paper}>
            <CircularProgress size={48} />
            <Typography variant="h6" style={{ marginTop: 16 }}>
              מעבד את התשלום...
            </Typography>
          </Paper>
        </div>
      );
    }

    const path = window.location.pathname;

    if (path === "/payment-link/success" && result) {
      return (
        <div className={classes.root}>
          <Paper className={classes.paper}>
            <div className={classes.successBox}>
              <Typography variant="h4" style={{ marginBottom: 16 }}>
                התשלום התקבל בהצלחה
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>אסמכתא:</strong> {result.paymentReference}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>עבור:</strong> {result.title || "תשלום מיוחד"}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>תיאור:</strong> {result.description}
              </Typography>
              <Typography variant="h6" style={{ marginTop: 12 }}>
                <strong>סכום ששולם:</strong> {formatAmount(result.amount)}
              </Typography>
              <Typography variant="body2" style={{ marginTop: 16 }}>
                אישור נשלח לכתובת האימייל שהוזנה.
              </Typography>
            </div>

            <div className={classes.actions}>
              <Button variant="contained" color="primary" onClick={() => (window.location.href = "/")}>
                חזרה לחנות
              </Button>
              <Button variant="outlined" onClick={this.goToRetry}>
                פתח שוב את הקישור
              </Button>
            </div>
          </Paper>
        </div>
      );
    }

    if (path === "/payment-link/failed") {
      return (
        <div className={classes.root}>
          <Paper className={classes.paper}>
            <div className={classes.errorBox}>
              <Typography variant="h5">התשלום נכשל</Typography>
              <Typography variant="body1" style={{ marginTop: 8 }}>
                לא ניתן היה להשלים את החיוב. אפשר לנסות שוב דרך הקישור שנשלח אליך.
              </Typography>
            </div>
            <div className={classes.actions}>
              <Button variant="contained" color="primary" onClick={this.goToRetry}>
                נסה שוב
              </Button>
              <Button variant="outlined" onClick={() => (window.location.href = "/")}>
                חזרה לחנות
              </Button>
            </div>
          </Paper>
        </div>
      );
    }

    if (path === "/payment-link/canceled") {
      return (
        <div className={classes.root}>
          <Paper className={classes.paper}>
            <div className={classes.cancelledBox}>
              <Typography variant="h5">התשלום בוטל</Typography>
              <Typography variant="body1" style={{ marginTop: 8 }}>
                תהליך התשלום הופסק לפני סיום. אפשר לחזור לקישור ולשלם שוב במידת הצורך.
              </Typography>
            </div>
            <div className={classes.actions}>
              <Button variant="contained" color="primary" onClick={this.goToRetry}>
                חזור לקישור
              </Button>
              <Button variant="outlined" onClick={() => (window.location.href = "/")}>
                חזרה לחנות
              </Button>
            </div>
          </Paper>
        </div>
      );
    }

    return (
      <div className={classes.root}>
        <Paper className={classes.paper}>
          <div className={classes.errorBox}>
            <Typography variant="h5">שגיאה</Typography>
            <Typography variant="body1" style={{ marginTop: 8 }}>
              {error || "אירעה שגיאה בעיבוד תוצאת התשלום."}
            </Typography>
          </div>
          <div className={classes.actions}>
            <Button variant="contained" color="primary" onClick={this.goToRetry}>
              חזור לקישור
            </Button>
            <Button variant="outlined" onClick={() => (window.location.href = "/")}>
              חזרה לחנות
            </Button>
          </div>
        </Paper>
      </div>
    );
  }
}

PaymentLinkResultPage.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(PaymentLinkResultPage);
